// src/pages/CheckoutSuccess.jsx
import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import {
  doc,
  onSnapshot,
  setDoc,
  getDoc,
  serverTimestamp,
  arrayUnion,
} from "firebase/firestore";
import { auth, db, callFunction } from "../firebase";

// ---- Route mapping ----
const ROUTES = {
  free: "/free-dashboard",
  personal: "/personal-dashboard",
  family: "/family-dashboard",
  school: "/school-dashboard",
};

const normalizeRoute = (v) => {
  const k = String(v || "").toLowerCase();
  if (k === "family") return ROUTES.family;
  if (k === "personal") return ROUTES.personal;
  if (k === "school") return ROUTES.school;
  if (k === "free") return ROUTES.free;
  return null;
};

const pickDashboardPath = (sub, planName) => {
  // Prefer subscription object (written by webhook/attach)
  if (sub) {
    const key = String(sub.packageKey || "").toLowerCase();
    const kind = String(sub.packageKind || "").toLowerCase();
    const familySeats = Number(sub.seats?.family || 0);

    if (kind === "school" || key.startsWith("school_")) return ROUTES.school;
    if (kind === "family" || key.startsWith("family") || familySeats >= 2) return ROUTES.family;
    if (kind === "personal" || key === "personal") return ROUTES.personal;
  }

  // Fallbacks if webhook hasn’t landed yet
  const sel = (localStorage.getItem("selectedPlan") || "").toLowerCase();
  if (sel.startsWith("school_")) return ROUTES.school;
  if (sel.startsWith("family")) return ROUTES.family;
  if (sel === "personal") return ROUTES.personal;

  const p = String(planName || "").toLowerCase();
  if (p.includes("school")) return ROUTES.school;
  if (p.includes("family")) return ROUTES.family;
  if (p.includes("personal")) return ROUTES.personal;

  const last = normalizeRoute(localStorage.getItem("lastDashboard"));
  if (last) return last;

  return ROUTES.free;
};

const formatDate = (ms) => {
  if (!ms) return null;
  try {
    const d = new Date(ms);
    return d.toLocaleDateString();
  } catch {
    return null;
  }
};

/**
 * Ensures the user has a school/org bootstrap:
 *  - creates/merges schools/{uid} with owner/admins
 *  - stamps users/{uid} with { orgId, schoolId, role: "school_admin" } ONLY if missing
 *  - caches schoolId in localStorage
 */
async function ensureSchoolBootstrapFor(uid) {
  if (!uid) return null;

  // 1) Create/merge the school doc first
  try {
    await setDoc(
      doc(db, "schools", uid),
      {
        ownerUid: uid,
        admins: arrayUnion(uid),
        createdAt: serverTimestamp(),
      },
      { merge: true }
    );
  } catch (e) {
    console.error("[CheckoutSuccess] create/merge schools doc failed:", e);
    throw e;
  }

  // 2) Stamp the user doc with orgId/schoolId/role ONLY if currently unset
  try {
    const uref = doc(db, "users", uid);
    const snap = await getDoc(uref);
    const curr = snap.exists() ? snap.data() || {} : {};

    const update = {};
    if (!curr.orgId) update.orgId = uid;
    if (!curr.schoolId) update.schoolId = uid;
    if (!curr.role) update.role = "school_admin"; // org-scoped admin, not platform admin

    if (Object.keys(update).length > 0) {
      await setDoc(uref, update, { merge: true });
    }
  } catch (e) {
    console.error("[CheckoutSuccess] stamp user with orgId/schoolId/role failed:", e);
    throw e;
  }

  // 3) Hint for routing
  try {
    localStorage.setItem("schoolId", uid);
  } catch {}

  return uid;
}

const CheckoutSuccess = () => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const qs = new URLSearchParams(search);

  // From Stripe redirects
  const sessionIdFromQS = qs.get("session_id") || "";
  const isTopup = qs.get("topup") === "1";
  const forcedTo = normalizeRoute(qs.get("to"));

  const [loading, setLoading] = useState(true);
  const [signedIn, setSignedIn] = useState(false);
  const [planName, setPlanName] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const sessionId = sessionIdFromQS;
  const [refreshTick, setRefreshTick] = useState(0);
  const [planTypeHint, setPlanTypeHint] = useState(null); // 'school' | 'consumer' | null

  // Early escape: don't stay on success if this isn't a Checkout return
useEffect(() => {
  // Only for non-topup, and no checkout session present
  if (!isTopup && !sessionIdFromQS) {
    const last = localStorage.getItem("lastDashboard");
    navigate(last || "/personal-dashboard", { replace: true });
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);


  // Listen for auth + user doc updates
  useEffect(() => {
    const off = onAuthStateChanged(auth, (user) => {
      setSignedIn(!!user);
      if (!user) {
        setLoading(false);
        return;
      }
      const ref = doc(db, "users", user.uid);
      const unsub = onSnapshot(ref, (snap) => {
        const d = snap.data() || {};
        setPlanName(d.plan || d.subscription?.displayName || null);
        setSubscription(d.subscription || null);
        setLoading(false);
      });
      return () => unsub();
    });
    return () => off();
  }, [refreshTick]);

  const dashboardPath = useMemo(() => {
    if (forcedTo) return forcedTo;
    return pickDashboardPath(subscription, planName);
  }, [forcedTo, subscription, planName]);

  // Attach subscription if needed; detect plan type and bootstrap school when applicable
  useEffect(() => {
    if (!signedIn || loading) return;
    if (isTopup) return; // add-ons have no subscription attachment

    const user = auth.currentUser;
    if (!user) return;

    // If there is a sessionId and we still don't see subscription, try attach
    if (sessionId && !(subscription?.packageKey || subscription?.packageKind)) {
      (async () => {
        try {
          // Optionally returns { planType: 'school'|'consumer', schoolId?: string }
          const data = await callFunction("attachSubscriptionFromSession", { sessionId });

          if (data?.planType) setPlanTypeHint(String(data.planType).toLowerCase());

          if (data?.planType === "school") {
            const existing = localStorage.getItem("schoolId");
            if (data?.schoolId) {
              localStorage.setItem("schoolId", data.schoolId);
            } else if (!existing) {
              await ensureSchoolBootstrapFor(user.uid);
            }
          }

          setRefreshTick((x) => x + 1); // trigger user doc refresh
        } catch (e) {
          // Attachment may already have happened; continue with fallbacks
          console.warn("[CheckoutSuccess] attachSubscriptionFromSession warn:", e?.message || e);
        }
      })();
    } else {
      // No session or already attached. If we believe this is a school plan, make sure we’re bootstrapped.
      const looksLikeSchool =
        planTypeHint === "school" ||
        subscription?.packageKind === "school" ||
        String(subscription?.packageKey || "").toLowerCase().startsWith("school_") ||
        (localStorage.getItem("selectedPlan") || "").toLowerCase().startsWith("school_") ||
        (planName || "").toLowerCase().includes("school");

      if (looksLikeSchool && !localStorage.getItem("schoolId")) {
        (async () => {
          try {
            await ensureSchoolBootstrapFor(user.uid);
            setRefreshTick((x) => x + 1);
          } catch (e) {
            console.warn("[CheckoutSuccess] school bootstrap after-attach warn:", e?.message || e);
          }
        })();
      }
    }
  }, [signedIn, loading, isTopup, sessionId, subscription, planTypeHint, planName]);

  // Auto-redirect logic
  useEffect(() => {
    if (!signedIn || loading) return;

    const goingToSchool = dashboardPath === ROUTES.school;

    // TOP-UP flow: send back to wherever the user belongs
    if (isTopup) {
      const t = setTimeout(() => navigate(dashboardPath, { replace: true }), 900);
      return () => clearTimeout(t);
    }

      // SUBSCRIPTION flow
  if (subscription?.packageKind || subscription?.packageKey) {
    if (goingToSchool) {
      const t = setTimeout(() => {
        if (!localStorage.getItem("schoolId") && auth.currentUser) {
          // ✅ Always guarantee a school doc + schoolId before routing
          ensureSchoolBootstrapFor(auth.currentUser.uid).finally(() => {
            navigate(ROUTES.school, { replace: true });
          });
        } else {
          navigate(ROUTES.school, { replace: true });
        }
      }, 1000);
      return () => clearTimeout(t);
    }

    // consumer/family/personal
    const t = setTimeout(() => navigate(dashboardPath, { replace: true }), 1000);
    return () => clearTimeout(t);
  }


      // Gentle fallback if we have any signal (e.g. webhook slow)
  const hasSignal =
    Boolean(planName) ||
    Boolean(localStorage.getItem("selectedPlan"));

  if (hasSignal) {
    const t = setTimeout(() => {
      if (goingToSchool && !localStorage.getItem("schoolId") && auth.currentUser) {
        // ✅ last-resort bootstrap for school plans
        ensureSchoolBootstrapFor(auth.currentUser.uid).finally(() => {
          navigate(ROUTES.school, { replace: true });
        });
      } else {
        navigate(dashboardPath, { replace: true });
      }
    }, 1800);
    return () => clearTimeout(t);
  }
}, [signedIn, loading, isTopup, subscription, planName, dashboardPath, planTypeHint, navigate]);


  const goDashboard = async () => {
    if (dashboardPath === ROUTES.school && !localStorage.getItem("schoolId") && auth.currentUser) {
      try { await ensureSchoolBootstrapFor(auth.currentUser.uid); } catch {}
    }
    navigate(dashboardPath);
  };

  const openPortal = async () => {
    try {
      const schoolId = localStorage.getItem("schoolId");
      const goingToSchool = (dashboardPath === ROUTES.school) || (planTypeHint === "school");
      if (schoolId && goingToSchool) {
        // Org (school) billing portal
        const data = await callFunction("createOrgPortalSession", {
          orgId: schoolId,
          returnUrl: window.location.origin + "/school-dashboard",
        });
        if (data?.url) {
          window.location.href = data.url;
          return;
        }
        throw new Error("No org portal URL returned");
      } else {
        // Consumer/family portal
        const data = await callFunction("createPortalSession", {
          returnUrl: window.location.origin + "/account",
        });
        if (data?.url) {
          window.location.href = data.url;
          return;
        }
        throw new Error("No portal URL returned");
      }
    } catch (e) {
      console.error(e);
      alert("Could not open billing portal. Please try again in a moment.");
    }
  };

  const refreshNow = () => setRefreshTick((x) => x + 1);

  const bg = require("../assets/images/landing_background.jpg");
  const renewOn = subscription?.currentPeriodEnd || null;

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        padding: 24,
        fontFamily: "Montserrat, sans-serif",
        backgroundImage: `url(${bg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div
        style={{
          maxWidth: 560,
          width: "100%",
          background: "rgba(255,255,255,0.95)",
          borderRadius: 12,
          boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
          padding: 24,
          textAlign: "center",
        }}
      >
        <h1 style={{ marginTop: 0 }}>
          {isTopup ? "Top-up successful 🎉" : "Payment successful 🎉"}
        </h1>

        {!signedIn && (
          <>
            <p>Thanks! Your payment has been received.</p>
            <p>
              Please <Link to="/auth">sign in</Link> to apply this to your account.
            </p>
            {sessionId ? (
              <p style={{ fontSize: 12, opacity: 0.8, marginTop: 12 }}>
                Session: {sessionId.slice(0, 12)}…
              </p>
            ) : null}
          </>
        )}

        {signedIn && loading && (
          <>
            <p>Thanks! Your payment has been received.</p>
            <p>
              {isTopup
                ? "We’re updating your balance… this usually takes a moment."
                : "We’re updating your account… this usually takes a moment."}
            </p>
            {sessionId ? (
              <p style={{ fontSize: 12, opacity: 0.8, marginTop: 12 }}>
                Session: {sessionId.slice(0, 12)}…
              </p>
            ) : null}
          </>
        )}

        {signedIn && !loading && (
          <>
            {isTopup ? (
              <>
                <p>
                  Your add-on was processed. If you don’t see the new balance yet,
                  it’ll appear shortly after the webhook updates your account.
                </p>
                <div
                  style={{
                    display: "flex",
                    gap: 12,
                    justifyContent: "center",
                    marginTop: 12,
                    flexWrap: "wrap",
                  }}
                >
                  <button className="subscription-button" onClick={goDashboard}>
                    Back to Dashboard
                  </button>
                  <button className="subscription-button" onClick={refreshNow}>
                    Refresh Balance
                  </button>
                  <button className="subscription-button" onClick={openPortal}>
                    Manage Billing
                  </button>
                </div>
              </>
            ) : (
              <>
                <p>Thanks! Your payment has been received and your account is now updated.</p>
                <div
                  style={{
                    margin: "16px auto",
                    padding: "12px 16px",
                    borderRadius: 10,
                    background: "#f6f7fb",
                    textAlign: "left",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: 6,
                    }}
                  >
                    <span style={{ opacity: 0.8 }}>Current package</span>
                    <strong>{planName || "—"}</strong>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: 6,
                    }}
                  >
                    <span style={{ opacity: 0.8 }}>Status</span>
                    <strong>{subscription?.status || "active"}</strong>
                  </div>
                  {renewOn ? (
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ opacity: 0.8 }}>Renews</span>
                      <strong>{formatDate(renewOn)}</strong>
                    </div>
                  ) : null}
                </div>

                <div
                  style={{
                    display: "flex",
                    gap: 12,
                    justifyContent: "center",
                    marginTop: 12,
                  }}
                >
                  <button className="subscription-button" onClick={goDashboard}>
                    Go to Dashboard
                  </button>
                  <button className="subscription-button" onClick={openPortal}>
                    Manage Billing
                  </button>
                </div>
              </>
            )}

            {sessionId ? (
              <p style={{ fontSize: 12, opacity: 0.8, marginTop: 12 }}>
                Session: {sessionId.slice(0, 12)}…
              </p>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
};

export default CheckoutSuccess;

